import React, { useReducer, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { TextArea } from '@pearsontechnology/aero-components';
import color from 'tinycolor2';

const START_EDITING = 'START_EDITING';
const CANCEL_EDITING = 'CANCEL_EDITING';
const FINISH_EDITING = 'FINISH_EDITING';
const UPDATE_VALUE = 'UPDATE_VALUE';

const NormalMode = styled.span`
  display: block;
  border-bottom: 1px dotted transparent;
  padding-bottom: 3px;
  flex: 0 1 auto;
  margin: 10px 10px 10px 0;
  line-height: 1.1;
  max-width: 46vw;
  overflow-wrap: break-word;
  word-break: break-word;
  &:hover {
    border-bottom: 1px dotted;
    cursor: pointer;
  }
  @media (max-width: 1300px) {
    max-width: 40vw;
  }
  @media (min-width: 1900px) {
    max-width: 60vw;
  }
`;

const EditMode = styled(TextArea)`
  border-bottom: 1px dotted;
  line-height: 1.1;
  display: block;
  padding: 0;
  background: ${(props) => color(props.theme['color.black']).setAlpha(0.1).toString()};
  flex: 0 1 auto;
  margin: 10px 10px 10px 0;

  &:focus {
    outline: none;
  }
`;

const reducer = (state, action) => {
  switch (action.type) {
    case (START_EDITING):
      return { ...state, editing: true, finished: false };
    case (CANCEL_EDITING):
      return { ...state, editing: false, value: action.value };
    case (FINISH_EDITING):
      return { ...state, editing: false, finished: true };
    case (UPDATE_VALUE):
      return { ...state, value: action.value };
    default:
      throw new Error();
  }
};

const InlineTextInput = ({ onChange, value }) => {
  const [state, dispatch] = useReducer(reducer, { editing: false, value });

  useEffect(() => {
    dispatch({ type: UPDATE_VALUE, value });
    return () => {};
  }, [value]);

  useEffect(() => {
    if (state.finished && state.value !== value) {
      state.finished = false;
      onChange(state.value);
    }
    return () => {};
  }, [onChange, state.finished, state.value, value]);

  const keyDown = (event) => {
    if (event.keyCode === 27) {
      dispatch({ type: CANCEL_EDITING, value });
    }
    if (event.keyCode === 13) {
      dispatch({ type: FINISH_EDITING });
    }
  };

  const onInputChanged = (event) => {
    const newValue = event.target.value.replace(/\n/g, '');
    dispatch({ type: UPDATE_VALUE, value: newValue });
  };

  const handleBlur = () => {
    dispatch({ type: FINISH_EDITING });
  };

  const handleFocus = (focusEvent) => {
    /**
     * To set the cursor at the end of Title in TextArea when focused
     */
    const title = focusEvent.target.value;
    // eslint-disable-next-line no-param-reassign
    focusEvent.target.value = '';
    // eslint-disable-next-line no-param-reassign
    focusEvent.target.value = title;
  };

  const renderNormal = () => (
    <NormalMode
      onClick={() => dispatch({ type: START_EDITING })}
    >{state.value}</NormalMode>
  );

  const renderEditing = () => (
    <EditMode
      autoFocus
      onKeyDown={keyDown}
      value={state.value}
      onChange={onInputChanged}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );

  return state.editing ? renderEditing() : renderNormal();
};

InlineTextInput.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default InlineTextInput;
